import { useEffect, useState, useRef } from "react";
import { FolderOpen, Upload, Camera, Loader2, Trash2, Eye, X, FileText, AlertTriangle, Calendar } from "lucide-react";
import { api, fmtDate } from "../lib/api";
import { toast } from "sonner";

const CATEGORIE = [
    "Certificato HACCP",
    "Attestato sanitario",
    "Licenza commerciale",
    "Contratto affitto",
    "Contratto fornitore",
    "Assicurazione",
    "Manuale HACCP firmato",
    "Visure / Camera Commercio",
    "Altro",
];

export default function Documenti() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [filter, setFilter] = useState("");
    const [detail, setDetail] = useState(null);

    const cameraRef = useRef(null);
    const fileRef = useRef(null);

    // Upload form
    const [open, setOpen] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const [form, setForm] = useState({ titolo: "", categoria: "Altro", data_scadenza: "", notify_days_before: 30, note: "" });

    async function load() {
        setLoading(true);
        try {
            const r = await api.get("/documents", { params: filter ? { categoria: filter } : {} });
            setItems(r.data);
        } catch {
            toast.error("Errore caricamento");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, [filter]);

    function onFilePicked(file) {
        if (!file) return;
        const ok = file.type.startsWith("image/") || file.type === "application/pdf";
        if (!ok) { toast.error("Carica una foto o un PDF"); return; }
        setPendingFile(file);
        setForm({ titolo: file.name.replace(/\.[^.]+$/, ""), categoria: "Altro", data_scadenza: "", notify_days_before: 30, note: "" });
        setOpen(true);
    }

    async function submit() {
        if (!pendingFile) return;
        if (!form.titolo.trim()) { toast.error("Titolo obbligatorio"); return; }
        setUploading(true);
        const fd = new FormData();
        fd.append("file", pendingFile);
        fd.append("titolo", form.titolo);
        fd.append("categoria", form.categoria);
        fd.append("data_scadenza", form.data_scadenza || "");
        fd.append("notify_days_before", String(parseInt(form.notify_days_before) || 30));
        fd.append("note", form.note || "");
        try {
            await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Documento caricato");
            setOpen(false);
            setPendingFile(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore upload");
        } finally {
            setUploading(false);
            if (cameraRef.current) cameraRef.current.value = "";
            if (fileRef.current) fileRef.current.value = "";
        }
    }

    async function view(id) {
        try {
            const r = await api.get(`/documents/${id}`);
            setDetail(r.data);
        } catch {
            toast.error("Errore");
        }
    }

    async function del(id) {
        if (!confirm("Eliminare il documento?")) return;
        await api.delete(`/documents/${id}`);
        toast.success("Eliminato");
        setDetail(null);
        load();
    }

    return (
        <div className="space-y-6 pb-6" data-testid="documenti-page">
            <section className="card-soft p-6 bg-gradient-to-br from-[#FFFCF6] to-[#F4EFE0] border-[#E4DEC3]">
                <div className="flex items-start gap-4 flex-wrap">
                    <div className="w-14 h-14 rounded-2xl bg-accent-warm grid place-items-center text-white shadow-md shrink-0">
                        <FolderOpen size={26} />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <h2 className="font-display text-xl font-semibold text-forest-dark">
                            Documenti del negozio
                        </h2>
                        <p className="text-sm text-stone-600 mt-1">
                            Carica e archivia certificati, contratti, attestati. Puoi anche assegnare una data di scadenza per ricevere avvisi.
                        </p>
                    </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                    <button onClick={() => cameraRef.current?.click()} className="btn-accent inline-flex items-center gap-2 text-sm" data-testid="doc-btn-camera">
                        <Camera size={16} /> Scatta foto
                    </button>
                    <button onClick={() => fileRef.current?.click()} className="btn-ghost inline-flex items-center gap-2 text-sm" data-testid="doc-btn-upload">
                        <Upload size={16} /> Carica file (foto / PDF)
                    </button>
                    <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onFilePicked(e.target.files?.[0])} data-testid="doc-input-camera" />
                    <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => onFilePicked(e.target.files?.[0])} data-testid="doc-input-file" />
                </div>
            </section>

            <section className="card-soft p-6" data-testid="docs-list">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <h2 className="font-display text-xl font-semibold text-forest-dark">
                        Archivio ({items.length})
                    </h2>
                    <select className="input-soft max-w-[220px]" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="docs-filter">
                        <option value="">Tutte le categorie</option>
                        {CATEGORIE.map((c) => <option key={c}>{c}</option>)}
                    </select>
                </div>

                {loading ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {[1,2,3].map((i) => <div key={i} className="h-32 bg-bone-2 rounded-xl animate-pulse" />)}
                    </div>
                ) : !items.length ? (
                    <div className="text-center py-12 text-stone-500">
                        <FolderOpen size={36} className="mx-auto opacity-40" />
                        <p className="text-sm mt-2">Nessun documento. Carica il primo!</p>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {items.map((d) => {
                            const exp = d.data_scadenza ? new Date(d.data_scadenza) : null;
                            const daysToExp = exp ? Math.floor((exp - new Date()) / 86400000) : null;
                            const expired = daysToExp != null && daysToExp < 0;
                            const expiring = daysToExp != null && daysToExp >= 0 && daysToExp <= 30;
                            return (
                                <div key={d.id} className="card-soft p-4 hover:-translate-y-0.5 transition-all" data-testid={`doc-card-${d.id}`}>
                                    <div className="flex items-start justify-between mb-2 gap-2">
                                        <div className="w-9 h-9 rounded-lg bg-forest-50 text-forest grid place-items-center shrink-0">
                                            <FileText size={16} />
                                        </div>
                                        <span className="chip bg-bone-2 text-stone-700 text-[10px]">{d.categoria}</span>
                                    </div>
                                    <h3 className="font-display font-semibold text-forest-dark text-sm leading-tight truncate" title={d.titolo}>
                                        {d.titolo}
                                    </h3>
                                    {d.data_scadenza && (
                                        <p className={`text-xs mt-1 inline-flex items-center gap-1 ${
                                            expired ? "text-red-700 font-semibold" : expiring ? "text-amber-700" : "text-stone-500"
                                        }`}>
                                            {expired || expiring ? <AlertTriangle size={11} /> : <Calendar size={11} />}
                                            Scade {fmtDate(d.data_scadenza)}
                                            {expired && " (scaduto)"}
                                        </p>
                                    )}
                                    <div className="flex gap-1 mt-3">
                                        <button onClick={() => view(d.id)} className="btn-ghost text-xs flex-1 inline-flex items-center justify-center gap-1" data-testid={`doc-view-${d.id}`}>
                                            <Eye size={12} /> Apri
                                        </button>
                                        <button onClick={() => del(d.id)} className="p-2 text-stone-400 hover:text-red-600 rounded-lg" data-testid={`doc-del-${d.id}`}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {open && pendingFile && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => { setOpen(false); setPendingFile(null); }} data-testid="doc-upload-modal">
                    <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="px-6 py-4 border-b border-soft flex items-center justify-between">
                            <h3 className="font-display text-lg font-semibold text-forest-dark">Dettagli documento</h3>
                            <button onClick={() => { setOpen(false); setPendingFile(null); }} className="p-2 hover:bg-bone-2 rounded-lg"><X size={18} /></button>
                        </div>
                        <div className="p-6 space-y-3">
                            <p className="text-xs text-stone-500">File: <strong>{pendingFile.name}</strong> ({Math.round(pendingFile.size / 1024)} KB)</p>
                            <label className="block">
                                <span className="text-xs font-semibold text-stone-600">Titolo *</span>
                                <input className="input-soft mt-1" value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} data-testid="doc-title" />
                            </label>
                            <label className="block">
                                <span className="text-xs font-semibold text-stone-600">Categoria</span>
                                <select className="input-soft mt-1" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} data-testid="doc-cat">
                                    {CATEGORIE.map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </label>
                            <label className="block">
                                <span className="text-xs font-semibold text-stone-600">Data di scadenza (opzionale)</span>
                                <input type="date" className="input-soft mt-1" value={form.data_scadenza} onChange={(e) => setForm({ ...form, data_scadenza: e.target.value })} data-testid="doc-exp" />
                            </label>
                            {form.data_scadenza && (
                                <label className="block">
                                    <span className="text-xs font-semibold text-stone-600">Avvisami quanti giorni prima della scadenza</span>
                                    <input
                                        type="number"
                                        min="1"
                                        max="365"
                                        className="input-soft mt-1"
                                        value={form.notify_days_before}
                                        onChange={(e) => setForm({ ...form, notify_days_before: e.target.value })}
                                        data-testid="doc-notify"
                                    />
                                    <span className="text-xs text-stone-500 mt-0.5 inline-block">
                                        Riceverai una notifica push al telefono nei {form.notify_days_before} giorni prima.
                                    </span>
                                </label>
                            )}
                            <label className="block">
                                <span className="text-xs font-semibold text-stone-600">Note</span>
                                <textarea className="input-soft mt-1" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="doc-note" />
                            </label>
                            <button onClick={submit} disabled={uploading} className="btn-primary w-full mt-2 inline-flex items-center justify-center gap-2" data-testid="doc-save">
                                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                                Carica documento
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {detail && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setDetail(null)} data-testid="doc-detail-modal">
                    <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="px-6 py-4 border-b border-soft flex items-center justify-between sticky top-0 bg-white z-10">
                            <div>
                                <h3 className="font-display text-lg font-semibold text-forest-dark">{detail.titolo}</h3>
                                <p className="text-xs text-stone-500">{detail.categoria}{detail.data_scadenza && ` · Scade ${fmtDate(detail.data_scadenza)}`}</p>
                            </div>
                            <button onClick={() => setDetail(null)} className="p-2 hover:bg-bone-2 rounded-lg"><X size={18} /></button>
                        </div>
                        <div className="p-6">
                            {detail.mime_type?.startsWith("image/") ? (
                                <img
                                    src={`data:${detail.mime_type};base64,${detail.file_b64}`}
                                    alt={detail.titolo}
                                    className="w-full max-h-[60vh] object-contain rounded-xl bg-bone-2 border border-soft"
                                />
                            ) : (
                                <div className="text-center py-8">
                                    <FileText size={48} className="mx-auto text-stone-300" />
                                    <p className="text-sm text-stone-500 mt-2">{detail.nome_file || "File"}</p>
                                    <a
                                        href={`data:${detail.mime_type};base64,${detail.file_b64}`}
                                        download={detail.nome_file || `${detail.titolo}.pdf`}
                                        className="btn-primary mt-4 inline-block text-sm"
                                    >
                                        Scarica file
                                    </a>
                                </div>
                            )}
                            {detail.note && (
                                <p className="text-sm text-stone-600 mt-4 bg-bone-2 rounded-xl p-3">{detail.note}</p>
                            )}
                            <button onClick={() => del(detail.id)} className="btn-ghost text-sm mt-4 inline-flex items-center gap-1 text-red-600">
                                <Trash2 size={14} /> Elimina documento
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
